import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Clipboard, HelpCircle, Mail, RefreshCw, Share2, X } from 'lucide-react';
import { useStore, selectFatigued } from '../store/useStore';
import type { Remediation } from '../types';
import type { ExportFormat } from '../engine/weekly';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Weekly wrap-up: fatigue audit, achievements digest, exports, rollover. */
export function RolloverModal() {
  const open = useStore((s) => s.rolloverOpen);
  const closeRollover = useStore((s) => s.closeRollover);
  const startWeekRollover = useStore((s) => s.startWeekRollover);
  const remediate = useStore((s) => s.remediate);
  const openHandOff = useStore((s) => s.openHandOff);
  const buildExport = useStore((s) => s.buildExport);
  const archive = useStore((s) => s.archive);
  const tasks = useStore((s) => s.tasks);

  const weekHistory = useStore((s) => s.weekHistory);
  const [copied, setCopied] = useState<string | null>(null);
  // Founder incident 16 Aug (register #36): the purple button executed the
  // rollover with no explanation of what was about to happen — a founder
  // with 52 rollovers blind-confirmed out of fatigue and thought his data
  // was gone (it wasn't; it never is). Two-stage confirm + explainer now.
  const [confirming, setConfirming] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);
  const fatigued = useMemo(() => selectFatigued(tasks), [tasks]);
  const openCount = useMemo(
    () => Object.values(tasks).filter((t) => !t.done).length,
    [tasks],
  );
  // Same-day rewrap guard: three wraps in 27 hours happened in the wild.
  const lastWrapAt = weekHistory[0]?.endedAt ?? 0;
  const wrappedRecently = lastWrapAt > 0 && Date.now() - lastWrapAt < 24 * 60 * 60 * 1000;

  // Fresh open = fresh state (the modal component stays mounted).
  useEffect(() => {
    if (open) {
      setConfirming(false);
      setExplainOpen(false);
    }
  }, [open]);

  const share = async (format: ExportFormat) => {
    const text = buildExport(format);
    if (format === 'email') {
      window.location.href = `mailto:?subject=${encodeURIComponent(
        'ZoneIn Weekly Report',
      )}&body=${encodeURIComponent(text)}`;
      return;
    }
    const ok = await copyText(text);
    setCopied(ok ? format : null);
    if (ok) setTimeout(() => setCopied(null), 2000);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 dark:bg-slate-950/90 p-5 backdrop-blur-md"
          onClick={closeRollover}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-sm space-y-4 overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-2xl"
          >
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-black text-slate-900 dark:text-white">
                <RefreshCw size={15} className="text-brand-600 dark:text-brand-400" />
                Start New Operating Week
                <button
                  type="button"
                  onClick={() => setExplainOpen((v) => !v)}
                  aria-label="What is the Wrap-Up?"
                  className="rounded-full p-0.5 text-brand-500 transition-colors hover:text-brand-600 dark:hover:text-brand-300"
                >
                  <HelpCircle size={15} />
                </button>
              </h3>
              <button
                onClick={closeRollover}
                aria-label="Close"
                className="rounded-lg p-1 text-slate-500 transition-colors hover:text-slate-900 dark:hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            {/* Explainer — founder-requested (register #36): educate, don't hide */}
            {explainOpen && (
              <div className="space-y-2 rounded-xl border border-brand-200 dark:border-brand-500/20 bg-brand-50 dark:bg-brand-900/15 p-3.5 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
                <h4 className="text-xs font-extrabold text-slate-900 dark:text-white">
                  What is the Wrap-Up?
                </h4>
                <p>
                  It closes your current operating week — like ruling off a page
                  in a paper planner. It only ever happens when you tap it.
                </p>
                <p>
                  <b>Completed tasks</b> are archived to your Mastery Log and
                  bound into your Notebook — kept permanently, steps and notes
                  included.
                </p>
                <p>
                  <b>Open tasks are never deleted.</b> They ROLL forward into
                  the new week with you. A ROLL ×2 badge just means "this task
                  has lived through two week-changes" — completely normal for
                  projects that span several weeks. It's an age counter, not a
                  criticism. Only tasks that keep rolling many times get
                  flagged above for a rethink.
                </p>
                <p>
                  After the wrap-up, the app shows your Mastery Log so you can
                  see the week you just closed. Your open tasks are waiting on
                  the Workspace.
                </p>
              </div>
            )}

            {/* Fatigue audit */}
            {fatigued.length > 0 && (
              <div className="space-y-2 rounded-xl border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-950/20 p-3.5">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={17} className="mt-0.5 shrink-0 animate-pulse text-red-600 dark:text-red-400" />
                  <div>
                    <h4 className="text-xs font-extrabold text-slate-900 dark:text-white">Rollover Fatigue Alert</h4>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      These tasks have rolled over several weeks without
                      completion. Audit them before transitioning:
                    </p>
                  </div>
                </div>
                {fatigued.map((task) => (
                  <div
                    key={task.id}
                    className="space-y-2 rounded-lg border border-red-200 dark:border-red-500/10 bg-slate-50 dark:bg-slate-950 p-3"
                  >
                    <p className="text-[11px] font-bold leading-tight text-red-600 dark:text-red-400">
                      "{task.text}" — rolled over {task.rolloverCount}×
                    </p>
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        const v = e.target.value as Remediation | '';
                        if (v) remediate(task.id, v);
                        // Weekly Insights (spec §3): "Delegate it" opens the
                        // hand-off fields on the card once the board shows.
                        if (v === 'delegate') openHandOff(task.id);
                      }}
                      className="w-full rounded border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1.5 text-[11px] text-slate-600 dark:text-slate-300 focus:outline-none"
                    >
                      <option value="">— Choose remediation strategy —</option>
                      <option value="split">Break into smaller tasks (Zone 2)</option>
                      <option value="delegate">Delegate it (Zone 3)</option>
                      <option value="drop">Drop completely (Zone 4)</option>
                      <option value="keep">Keep rolling (ignore fatigue)</option>
                    </select>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              Transitioning archives this week's achievements, rolls your open
              tasks into the new week (nothing is deleted), and then shows your
              Mastery Log.
            </p>

            {/* Achievements digest */}
            <div className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3">
              <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-brand-600 dark:text-brand-400">
                <span>Achievements This Week</span>
                <span>{archive.length} items</span>
              </div>
              <div className="max-h-20 overflow-y-auto text-[11px] leading-normal text-slate-500 dark:text-slate-400">
                {archive.length === 0
                  ? 'No accomplishments marked yet — check off tasks before rolling over.'
                  : archive.map((t, i) => <p key={i}>✓ {t.text}</p>)}
              </div>
            </div>

            {/* Exports */}
            <div className="space-y-2">
              {(
                [
                  { format: 'keep' as const, icon: Clipboard, label: 'Copy for Google Keep' },
                  { format: 'slack' as const, icon: Share2, label: 'Copy Slack (mrkdwn) format' },
                  { format: 'teams' as const, icon: Share2, label: 'Copy MS Teams format' },
                  { format: 'email' as const, icon: Mail, label: 'Email weekly digest' },
                ] as const
              ).map(({ format, icon: Icon, label }) => (
                <button
                  key={format}
                  onClick={() => void share(format)}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 py-2.5 text-xs font-bold text-slate-900 dark:text-white transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
                >
                  <Icon size={14} className="text-brand-600 dark:text-brand-400" />
                  {copied === format ? 'Copied! ✓' : label}
                </button>
              ))}

              {!confirming ? (
                <button
                  onClick={() => setConfirming(true)}
                  className="w-full rounded-xl bg-brand-600 py-3.5 text-xs font-bold text-white shadow-lg shadow-brand-600/20 transition-all hover:bg-brand-500 active:scale-[0.98]"
                >
                  Start New Week…
                </button>
              ) : (
                <div className="space-y-2 rounded-xl border border-brand-200 dark:border-brand-500/25 bg-brand-50 dark:bg-brand-900/15 p-3.5">
                  <h4 className="text-xs font-extrabold text-slate-900 dark:text-white">
                    Here's exactly what will happen:
                  </h4>
                  <ul className="space-y-1 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
                    <li>
                      ✓ <b>{archive.length}</b> completed task
                      {archive.length === 1 ? '' : 's'} → archived to your
                      Mastery Log and bound into your Notebook, permanently.
                    </li>
                    <li>
                      → <b>{openCount}</b> open task
                      {openCount === 1 ? '' : 's'} → roll into the new week
                      with you. <b>Nothing is deleted.</b>
                    </li>
                    <li>→ You'll then see your Mastery Log.</li>
                  </ul>
                  {wrappedRecently && (
                    <p className="rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 px-2.5 py-2 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                      ⚠ You already closed a week in the last 24 hours
                      {weekHistory[0] ? ` (${weekHistory[0].label})` : ''}.
                      Wrapping again creates another archived week — usually
                      you only need this once a week.
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirming(false)}
                      className="flex-1 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Go back
                    </button>
                    <button
                      onClick={startWeekRollover}
                      className="flex-1 rounded-xl bg-brand-600 py-2.5 text-xs font-bold text-white shadow-lg shadow-brand-600/20 transition-all hover:bg-brand-500 active:scale-[0.98]"
                    >
                      Yes — start the new week
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
